import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Receipt, StackSimple, ClockCounterClockwise } from "@phosphor-icons/react";

const TYPE_STYLES = {
  credit: "text-[hsl(var(--success))]",
  reserved_hold: "text-[hsl(var(--warning))]",
  release: "text-primary",
  settlement: "text-muted-foreground",
};

export default function Reports() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const canAudit = user?.role !== "customer";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/ledger"), api.get("/ledger/reservations")];
      if (canAudit) reqs.push(api.get("/audit"));
      const [l, r, a] = await Promise.all(reqs);
      setEntries(l.data); setReservations(r.data); if (a) setLogs(a.data);
    } catch (e) {
      setEntries([]); setReservations([]); setLogs([]);
    } finally { setLoading(false); }
  }, [canAudit]);
  useEffect(() => { load(); }, [load]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  return (
    <div>
      <PageHeader title="Reports" subtitle="Billing ledger, reservation holds and audit trail" />
      <Tabs defaultValue="ledger">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="tab-reservations">Reservations</TabsTrigger>
          {canAudit && <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>}
        </TabsList>

        <TabsContent value="ledger" className="mt-6">
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
            : entries.length === 0 ? <EmptyState testid="ledger-empty" icon={Receipt} title="No transactions yet" description="Credits, holds, releases and settlements appear here." />
              : <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="ledger-table">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Available after</TableHead><TableHead>Group</TableHead></TableRow></TableHeader>
                  <TableBody>{entries.map((e) => (
                    <TableRow key={e.id} className="hover:bg-muted/40" data-testid={`ledger-row-${e.id}`}>
                      <TableCell className="text-sm text-muted-foreground">{fmt(e.created_at)}</TableCell>
                      <TableCell className={`font-medium capitalize ${TYPE_STYLES[e.type] || ""}`}>{e.type.replace("_", " ")}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(e.amount_minor)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{money(e.balance_after_minor)}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">{e.reservation_group_id ? e.reservation_group_id.slice(0, 8) : "—"}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              </div>}
        </TabsContent>

        <TabsContent value="reservations" className="mt-6">
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
            : reservations.length === 0 ? <EmptyState testid="reservations-empty" icon={StackSimple} title="No active reservations" description="Activating campaign concurrency places a linked dual hold." />
              : <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="reservations-table">
                <Table>
                  <TableHeader><TableRow><TableHead>Group</TableHead><TableHead className="text-center">Concurrency</TableHead><TableHead className="text-right">Reseller hold</TableHead><TableHead className="text-right">Customer hold</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>{reservations.map((r) => (
                    <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`reservation-row-${r.id}`}>
                      <TableCell className="text-xs font-mono text-muted-foreground">{r.id.slice(0, 8)}</TableCell>
                      <TableCell className="text-center tabular-nums">{r.concurrency}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.reseller_hold_minor)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.customer_hold_minor)}</TableCell>
                      <TableCell className="capitalize text-sm">{r.status}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              </div>}
        </TabsContent>

        {canAudit && (
          <TabsContent value="audit" className="mt-6">
            {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
              : logs.length === 0 ? <EmptyState testid="audit-empty" icon={ClockCounterClockwise} title="No audit events yet" description="Tenant, pricing and wallet operations are recorded here." />
                : <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="audit-table">
                  <Table>
                    <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead></TableRow></TableHeader>
                    <TableBody>{logs.map((l) => (
                      <TableRow key={l.id} className="hover:bg-muted/40" data-testid={`audit-row-${l.id}`}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                        <TableCell className="text-sm">{l.actor_email || "system"}</TableCell>
                        <TableCell className="font-medium text-sm">{l.action}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{l.entity_type}</TableCell>
                      </TableRow>))}</TableBody>
                  </Table>
                </div>}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
